import PopulatedFrom, { AllowedPopulatedFromSources} from "@/components/DesignLibrary/Atoms/shared/PopulatedFrom";
import type { Questionnaire } from '@/typings/Questionnaire';
import getAiAgents from '@tsClient/questionnaires/getAiAgents';

export const determineType = (autofill: Questionnaire.FieldCommon['autofill']): AllowedPopulatedFromSources | undefined => {
  if (autofill?.action === 'conditional-fill' && autofill.expression?.variables[0]?.referenceType !== 'data-reference-slug') {
    return 'conditional-fill';
  }
  if (autofill?.action === 'get-filing-district' || autofill?.action === 'get-filing-county' || autofill?.action === 'ai-agent') {
    return 'ai';
  }
  if (autofill?.action === 'calculation') {
    return 'calculation';
  }
  if(autofill?.populateFrom === 'glade-questionnaire') {
    return 'noodle-questionnaire';
  }
  if(autofill?.action === 'fill-from-internal-data') {
    return 'internal-data';
  }
  const referenceType = autofill?.expression?.variables?.sort((variable) =>
    variable?.referenceType === 'data-reference-slug' ? -1 : 0,
  )?.[0]?.referenceType;

  if (
    referenceType && ['credit-report', 'data-reference-slug', 'field-template-key', 'workflow-context','means-test-data'].includes(referenceType)
  ) {
    return referenceType as AllowedPopulatedFromSources;
  }

  return undefined;
};

const renderPopulatedFrom = ({ aiAgents, field, prominent, canReset, onClick }: {
  aiAgents: Awaited<ReturnType<typeof getAiAgents>>['items'] | null;
  field: Questionnaire.FieldCommon,
  prominent: boolean,
  canReset?:boolean,
  onClick?: (e: React.MouseEvent) => void,
}): React.ReactElement | null => {
  const aiAgent = aiAgents?.find((a) => a.fieldOutputs.includes(field.key));
  const renderAutofill = (autofill: Questionnaire.FieldCommon['autofill'], index: number = 0): React.ReactElement => {
    const tooltip = autofill?.description ? { tooltip: autofill.description } : {};

    return (
      <PopulatedFrom
        key={autofill?.id || index}
        itemId={autofill?.expression?.variables[0]?.referenceId}
        type={determineType(autofill)}
        prominent={prominent}
        canReset={canReset}
        onClick={onClick}
        field={field}
        {...tooltip}
      />
    );
  };

  if (field.listItemTemplates && field.listItemTemplates.length > 0 && prominent) {
    const seenTypes = new Set();
    return <>
      {field.listItemTemplates
        .filter(listItemTemplate => {
          const type = determineType(listItemTemplate.autofill);
          if (type && !seenTypes.has(type)) {
            seenTypes.add(type);
            return true;
          }
          return false;
        })
        .map(({ autofill }, index) => renderAutofill(autofill, index))}
    </>;
  }

  if (field.autofill) {
    return renderAutofill(field.autofill);
  }

  if (aiAgent) {
    return (
      <PopulatedFrom
        key={aiAgent.id}
        itemId={aiAgent.id}
        type={'ai'}
        prominent={prominent}
        canReset={canReset}
        onClick={onClick}
        field={field}
      />
    );
  }

  return null;
};

export default renderPopulatedFrom;
